import React, { forwardRef, type PropsWithChildren } from 'react';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive, type TooltipPrimitiveProps } from '@atlaskit/tooltip';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { Illustration } from '@jira/illustrations__illustration/src/ui/index.tsx';
import treeViewImageLight from '@jira/illustrations__illustrations/src/ui/adg4/jira/spots/software/assets/jpd-treeview-spotlight-image.svg';
import treeViewImageDark from '@jira/illustrations__illustrations/src/ui/adg4/jira/spots/software/assets/jpd-treeview-spotlight-image-dark.svg';
import { VIEW_KIND_TREE } from '@jira/polaris__domain-view/src/viewKindTimeline.constants.tsx';
import { useIsViewKindLocked } from '../../../common/utils/useIsViewKindLocked.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import Skeleton from '@atlaskit/skeleton';
import { UpgradeButton } from '@jira/polaris__component-entitlements/src/UpgradeButton.tsx';

import { SpotlightPrimaryLink } from '@atlaskit/spotlight/primary-link';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';

const LEARN_MORE_URL =
	'https://community.atlassian.com/forums/Jira-Product-Discovery-articles/The-tree-view-is-now-generally-available-Premium-plan/ba-p/3249554#M4142';

const tooltipTitle = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.tree-view-tooltip-title',
	defaultMessage: 'Tree view has landed in Premium',
	description: 'Title for the tree view premium upsell tooltip',
});

const tooltipDescription = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.tree-view-tooltip-description',
	defaultMessage: 'Connect customer problems to solutions and experiments in a visual hierarchy.',
	description: 'Description for the tree view premium upsell tooltip',
});

const tooltipLearnMoreButton = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.tree-view-tooltip-learn-more-button',
	defaultMessage: 'Learn more',
	description: 'Button label to learn more about the tree view premium feature in tooltip',
});

const tooltipIllustrationAlt = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.create-view-menu.menu.tree-view-tooltip-illustration-alt',
	defaultMessage: 'Tree view premium illustration',
	description: 'Alt text for the tree view illustration in the premium upsell tooltip',
});

const styles = cssMap({
	tooltipContainer: {
		width: '295px',
		borderRadius: token('radius.large'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.150'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		boxShadow: token('elevation.shadow.overflow'),
		overflow: 'hidden',
		boxSizing: 'border-box',
	},
});

const TooltipContainer = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(
	({ children, ...rest }, ref) => (
		<TooltipPrimitive {...rest} ref={ref}>
			<Box backgroundColor="color.background.neutral.bold" xcss={styles.tooltipContainer}>
				{children}
			</Box>
		</TooltipPrimitive>
	),
);

const LearnMoreButton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<SpotlightPrimaryLink href={LEARN_MORE_URL} appearance="outline">
			{formatMessage(tooltipLearnMoreButton)}
		</SpotlightPrimaryLink>
	);
};

const TreeViewLockedTooltipContent = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<>
			<SpotlightHeader>
				<SpotlightHeadline>{formatMessage(tooltipTitle)}</SpotlightHeadline>
			</SpotlightHeader>
			<Illustration
				srcLight={treeViewImageLight}
				srcDark={treeViewImageDark}
				alt={formatMessage(tooltipIllustrationAlt)}
				width={295}
				height={135}
			/>
			<SpotlightBody>{formatMessage(tooltipDescription)}</SpotlightBody>
			<SpotlightFooter>
				<SpotlightActions>
					<JSErrorBoundary
						id="jpd.nav-views.tree-view.upgrade-button"
						teamName="JPD - Sirius"
						packageName="jiraNavigationAppsSidebarNav4SidebarsCommonJpdNavViews"
						fallback="unmount"
					>
						<Placeholder name="upgrade-button" fallback={<Skeleton width="150px" height="22px" />}>
							<UpgradeButton appearance="spotlight" unavailablePlaceholder={<LearnMoreButton />} />
						</Placeholder>
					</JSErrorBoundary>
				</SpotlightActions>
			</SpotlightFooter>
		</>
	);
};

export const TreeViewTooltip = ({ children }: PropsWithChildren): React.ReactNode => {
	const isViewKindLocked = useIsViewKindLocked();

	if (isViewKindLocked(VIEW_KIND_TREE)) {
		return (
			<Tooltip
				position="right"
				content={<TreeViewLockedTooltipContent />}
				component={TooltipContainer}
			>
				{/* wrap with span so the tooltip can be triggered by keyboard events on disabled children */}
				<span>{children}</span>
			</Tooltip>
		);
	}

	return children;
};
